import React, { useState, useEffect } from 'react';
import { 
  Users, 
  Building, 
  TrendingUp, 
  Shield, 
  Activity,
  Server,
  Database,
  Settings,
  UserPlus,
  PlusCircle,
  Eye,
  AlertCircle
} from 'lucide-react';
import { adminService } from '../api/adminService';
import { authService } from '../api/authService';
import { useNotification } from '../contexts/NotificationContext';
import { useNavigate } from 'react-router-dom';

export default function AdminHome() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalTenants: 0,
    activeUsers: 0,
    adminUsers: 0
  });
  const [loading, setLoading] = useState(true);
  const [recentActivities, setRecentActivities] = useState([]);
  const [systemHealth, setSystemHealth] = useState({
    api: 'online',
    database: 'online',
    lastBackup: '2 hours ago'
  });
  const { showError } = useNotification();

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Load users and tenants data
      const [usersResponse, tenantsResponse] = await Promise.all([
        adminService.users.list({ limit: 1000 }).catch(() => null),
        adminService.tenants.list({ limit: 1000 }).catch(() => null)
      ]);

      const users = usersResponse?.data || [];
      const tenants = tenantsResponse?.data || [];

      // Calculate statistics
      const totalUsers = users.length;
      const adminUsers = users.filter(user => user.is_admin || user.is_system_admin).length;
      const activeUsers = users.filter(user => user.created_at).length;
      const totalTenants = tenants.length;

      setStats({
        totalUsers,
        totalTenants,
        activeUsers,
        adminUsers
      });

      // Mock recent activities (in real app, this would come from API)
      setRecentActivities([
        { id: 1, type: 'user_created', message: 'New user created: john@example.com', time: '5 minutes ago' },
        { id: 2, type: 'tenant_created', message: 'New tenant created: Acme Corp', time: '1 hour ago' },
        { id: 3, type: 'user_login', message: 'Admin login: admin@system.com', time: '2 hours ago' },
        { id: 4, type: 'settings_updated', message: 'System settings updated', time: '1 day ago' }
      ]);

    } catch (error) {
      console.error('Error loading dashboard data:', error);
      // Don't show error to user, just set defaults
      setStats({
        totalUsers: 0,
        totalTenants: 0,
        activeUsers: 0,
        adminUsers: 0
      });
    } finally {
      setLoading(false);
    }
  };
  const getActivityIcon = (type) => {
    switch (type) {
      case 'user_created':
        return <UserPlus className="h-4 w-4" />;
      case 'tenant_created':
        return <PlusCircle className="h-4 w-4" />;
      case 'user_login':
        return <Eye className="h-4 w-4" />;
      case 'settings_updated':
        return <Settings className="h-4 w-4" />;
      default:
        return <Activity className="h-4 w-4" />;
    }
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-50 p-6">
        <div className="mx-auto max-w-7xl">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
            <p className="mt-2 text-gray-600">
              Sistem administrasi platform POS AsaLink
            </p>
          </div>

          {/* Statistics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white overflow-hidden shadow-sm rounded-lg border">
              <div className="p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="p-3 bg-blue-50 rounded-lg">
                      <Users className="h-6 w-6 text-blue-600" />
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">Total Users</dt>
                      <dd className="text-2xl font-bold text-gray-900">
                        {loading ? '...' : stats.totalUsers.toLocaleString()}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow-sm rounded-lg border">
              <div className="p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="p-3 bg-green-50 rounded-lg">
                      <Building className="h-6 w-6 text-green-600" />
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">Total Tenants</dt>
                      <dd className="text-2xl font-bold text-gray-900">
                        {loading ? '...' : stats.totalTenants.toLocaleString()}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow-sm rounded-lg border">
              <div className="p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="p-3 bg-orange-50 rounded-lg">
                      <TrendingUp className="h-6 w-6 text-orange-600" />
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">Active Users</dt>
                      <dd className="text-2xl font-bold text-gray-900">
                        {loading ? '...' : stats.activeUsers.toLocaleString()}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow-sm rounded-lg border">
              <div className="p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="p-3 bg-purple-50 rounded-lg">
                      <Shield className="h-6 w-6 text-purple-600" />
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">Admin Users</dt>
                      <dd className="text-2xl font-bold text-gray-900">
                        {loading ? '...' : stats.adminUsers.toLocaleString()}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Quick Actions */}
            <div className="lg:col-span-1">
              <div className="bg-white shadow-sm rounded-lg border">
                <div className="p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h3>
                  <div className="space-y-3">
                    <button
                      onClick={() => navigate('/admin/users')}
                      className="w-full flex items-center justify-between p-3 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                    >
                      <div className="flex items-center">
                        <Users className="h-5 w-5 text-blue-600 mr-3" />
                        <span className="text-blue-900 font-medium">Manage Users</span>
                      </div>
                    </button>
                    
                    <button
                      onClick={() => navigate('/admin/tenants')}
                      className="w-full flex items-center justify-between p-3 bg-green-50 hover:bg-green-100 rounded-lg transition-colors"
                    >
                      <div className="flex items-center">
                        <Building className="h-5 w-5 text-green-600 mr-3" />
                        <span className="text-green-900 font-medium">Manage Tenants</span>
                      </div>
                    </button>
                    
                    <button
                      onClick={() => navigate('/admin/settings')}
                      className="w-full flex items-center justify-between p-3 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors"
                    >
                      <div className="flex items-center">
                        <Settings className="h-5 w-5 text-purple-600 mr-3" />
                        <span className="text-purple-900 font-medium">System Settings</span>
                      </div>
                    </button>
                  </div>
                </div>
              </div>

              {/* System Health */}
              <div className="bg-white shadow-sm rounded-lg border mt-6">
                <div className="p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">System Health</h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <Server className="h-5 w-5 text-gray-400 mr-3" />
                        <span className="text-gray-700">API Status</span>
                      </div>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        systemHealth.api === 'online' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {systemHealth.api}
                      </span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <Database className="h-5 w-5 text-gray-400 mr-3" />
                        <span className="text-gray-700">Database</span>
                      </div>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        systemHealth.database === 'online' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {systemHealth.database}
                      </span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <Shield className="h-5 w-5 text-gray-400 mr-3" />
                        <span className="text-gray-700">Last Backup</span>
                      </div>
                      <span className="text-sm text-gray-500">{systemHealth.lastBackup}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Activities */}
            <div className="lg:col-span-2">
              <div className="bg-white shadow-sm rounded-lg border">
                <div className="p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Activities</h3>
                  
                  {loading ? (
                    <div className="space-y-3">
                      {[...Array(4)].map((_, i) => (
                        <div key={i} className="animate-pulse flex items-center p-3">
                          <div className="rounded-full bg-gray-200 h-8 w-8"></div>
                          <div className="ml-4 space-y-2 flex-1">
                            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                            <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {recentActivities.map((activity) => (
                        <div key={activity.id} className="flex items-center p-3 bg-gray-50 rounded-lg">
                          <div className={`p-2 rounded-full ${
                            activity.type === 'user_created' ? 'bg-blue-100 text-blue-600' :
                            activity.type === 'tenant_created' ? 'bg-green-100 text-green-600' :
                            activity.type === 'user_login' ? 'bg-purple-100 text-purple-600' :
                            'bg-gray-100 text-gray-600'
                          }`}>
                            {getActivityIcon(activity.type)}
                          </div>
                          <div className="ml-4 flex-1">
                            <p className="text-sm font-medium text-gray-900">{activity.message}</p>
                            <p className="text-xs text-gray-500">{activity.time}</p>
                          </div>
                        </div>
                      ))}
                      
                      {recentActivities.length === 0 && (
                        <div className="text-center py-8 text-gray-500">
                          <Activity className="h-8 w-8 mx-auto mb-2 opacity-50" />
                          <p>No recent activities</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}